import Box from '@app/components/common/Box';
import Button from '@app/components/common/Button';
import Card from '@app/components/common/Card';
import Divider from '@app/components/common/Divider';
import Grid from '@app/components/common/Grid';
import Icon from '@app/components/common/Icon';
import MainScreenWithFilterContainer from '@app/components/common/MainScreenWithFilterContainer';
import { IMainScreenWithFilterContainer } from '@app/components/common/MainScreenWithFilterContainer/MainScreenWithFilterContainer';
import SelectionModal from '@app/components/common/SelectionModal';
import { IItemSelection } from '@app/components/common/SelectionModal/GenericSelectionModal/types';
import Typography from '@app/components/common/Typography';
import {
  SELECTION_MULTIPLE_BUTTON_ID,
  SELECTION_NONE_BUTTON_ID,
} from '@app/constants';
import { getMainBreadcrumbBucket } from '@app/helpers/factories/menu';
import {
  getPathWithOrgData,
  openNewTabWithOrgData,
} from '@app/helpers/navigation';
import useNavigateWithOrg from '@app/hooks/useNavigateWithOrg';
import useRefreshMenuWeb from '@app/hooks/useRefreshMenuWeb';
import { actionCreatorsMenuWeb } from '@app/state';
import { showToast } from '@app/state/app/actions';
import {
  selectBreadCrumbs,
  selectCardItemList,
  selectCategoriesDropdownList,
  selectIsMenuMasterMode,
  selectMenuTypesDropdownList,
  selectMenuVersion,
  selectReportingGroupsDropdownList,
  selectSubcategoriesDropdownList,
} from '@app/state/menu/menuSelectors';
import { useAppDispatch } from '@app/state/store';
import { selectCanLoggedInUserMassUpdate } from '@app/state/users/usersSelectors';
import { bindActionCreators } from '@reduxjs/toolkit';
import {
  BreadCrumbAction,
  CONFIRMATION_MODAL_INITIAL_STATE,
  IItemCard,
  IItemTableRow,
  actionCreatorsApp,
  actionCreatorsMenu,
} from '@westondev/tableturn-core';
import intersection from 'lodash/intersection';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { WithTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import ItemMassUpdateModal from '../ItemMassUpdateModal';
import ItemsTable from './ItemsTable';

const ItemsList = ({ t }: WithTranslation) => {
  // Redux
  const appDispatch = useAppDispatch();
  const dispatch = useDispatch();
  const onRefresh = useRefreshMenuWeb();

  const {
    itemMassUpdateConfirmationWeb,
    assignItemsToLocationWeb,
    addBreadcrumbLocationWeb,
    itemsMassUpdateStatus,
    deleteBucketItemsWeb,
    itemMassUpdateWeb,
  } = bindActionCreators(actionCreatorsMenuWeb, dispatch);

  const { setShowConfirmationModal } = bindActionCreators(
    actionCreatorsApp,
    useDispatch(),
  );

  const breadcrumbs = useSelector(selectBreadCrumbs);
  const itemCardList = useSelector(selectCardItemList);
  const categoriesOptions = useSelector(selectCategoriesDropdownList);
  const reportingGroupsOptions = useSelector(selectReportingGroupsDropdownList);
  const subcategoriesOptions = useSelector(selectSubcategoriesDropdownList);
  const menuTypesOptions = useSelector(selectMenuTypesDropdownList);
  const isMasterMode = useSelector(selectIsMenuMasterMode);
  const menuVersion = useSelector(selectMenuVersion);
  const canMassUpdate = useSelector(selectCanLoggedInUserMassUpdate);
  // Local state
  const navigate = useNavigateWithOrg();
  const [additionModalState, setAdditionModalState] = useState(false);
  const [selectedMenuTypeIds, setSelectedMenuTypeIds] = useState<
    null | number[]
  >(null);
  const [massUpdate, setMassUpdate] = useState(false);
  const [selectingMode, setSelectingMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState<IItemTableRow[]>([]);
  const [showListView] = useState(true);
  const [runUnSelectAllRows, setRunUnSelectAllRows] = useState(false);

  const categoriesSet = useMemo(
    () =>
      new Set(
        (selectedMenuTypeIds !== null
          ? itemCardList.filter(
              item =>
                intersection(item.menuTypeId, selectedMenuTypeIds).length > 0,
            )
          : itemCardList
        )
          .map(item => item.categoryId)
          .flat(),
      ),
    [itemCardList, selectedMenuTypeIds],
  );

  const reportingGroupsSet = useMemo(
    () => new Set(itemCardList.map(item => item.reportingGroupIds).flat()),
    [itemCardList],
  );

  const categoriesOptionsFiltered = useMemo(
    () =>
      categoriesOptions.filter(category => categoriesSet.has(category.value)),
    [categoriesOptions, categoriesSet],
  );

  const reportingGroupsOptionsFiltered = useMemo(
    () =>
      reportingGroupsOptions.filter(reportingGroup =>
        reportingGroupsSet.has(reportingGroup.value),
      ),
    [reportingGroupsOptions, reportingGroupsSet],
  );

  const menusSet = useMemo(
    () => new Set(itemCardList.map(item => item.menuTypeId).flat()),
    [itemCardList],
  );

  const menusOptionsFiltered = useMemo(
    () => menuTypesOptions.filter(menu => menusSet.has(menu.value)),
    [menuTypesOptions, menusSet],
  );

  const dropdowns =
    useMemo((): IMainScreenWithFilterContainer<IItemCard>['dropdowns'] => {
      const specialButtonsOptions = [
        { label: t('commonTexts.none'), value: SELECTION_NONE_BUTTON_ID },
        {
          label: t('commonTexts.multiple'),
          value: SELECTION_MULTIPLE_BUTTON_ID,
        },
      ];
      return [
        {
          id: 'menuTypeId',
          data: [
            [{ label: t('commonTexts.all'), value: 0 }],
            menusOptionsFiltered,
          ],
          label: 'Menu',
          defaultValue: null,
          dataExample: 0,
          bucket: 'menuTypes',
          isMultiSelect: true,
        },
        {
          id: 'categoryId',
          data: [
            specialButtonsOptions,
            [{ label: t('commonTexts.all'), value: 0 }],
            categoriesOptionsFiltered,
          ],
          label: 'Category',
          defaultValue: null,
          dataExample: 0,
          bucket: 'categories',
          isMultiSelect: true,
          showSpecialNoneButton: true,
          showSpecialMultipleButton: true,
        },
        {
          id: 'subcategoryId',
          data: [],
          getData: ({ categoryId: categoryIdFilter }) => {
            let _subcategoriesOptions: typeof subcategoriesOptions = [];
            if (typeof categoryIdFilter !== 'string') {
              _subcategoriesOptions = subcategoriesOptions.filter(option =>
                Array.isArray(categoryIdFilter)
                  ? categoryIdFilter.includes(option.categoryId)
                  : option.categoryId === categoryIdFilter,
              );
            }
            return [
              specialButtonsOptions,
              [{ label: t('commonTexts.all'), value: 0 }],
              _subcategoriesOptions,
            ];
          },
          label: 'Subcategory',
          defaultValue: null,
          dataExample: 0,
          getIsDisabled: (data, { categoryId: categoryIdFilter }) => {
            if (typeof categoryIdFilter === 'string' || categoryIdFilter === 0)
              return true;
            return !data[1].length;
          },
          bucket: 'subcategories',
          isMultiSelect: true,
          showSpecialNoneButton: true,
          showSpecialMultipleButton: true,
        },
        {
          id: 'reportingGroupIds',
          data: [
            specialButtonsOptions,
            [{ label: t('commonTexts.all'), value: 0 }],
            reportingGroupsOptionsFiltered,
          ],
          label: 'Reporting Groups',
          defaultValue: null,
          dataExample: 0,
          bucket: 'reportingGroups',
          isMultiSelect: true,
          showSpecialNoneButton: true,
          showSpecialMultipleButton: true,
        },
        {
          id: 'statuses',
          data: [
            [
              { label: t('commonTexts.all'), value: 0 },
              { label: t('commonTexts.active'), value: 1 },
              { label: t('commonTexts.inactive'), value: 2 },
            ],
          ],
          label: 'Status',
          defaultValue: 1,
          dataExample: 0,
          isMultiSelect: false,
        },
      ];
    }, [
      categoriesOptionsFiltered,
      subcategoriesOptions,
      t,
      menusOptionsFiltered,
      reportingGroupsOptionsFiltered,
    ]);

  const { setBreadCrumb, resetChangeMassUpdateData } = bindActionCreators(
    actionCreatorsMenu,
    dispatch,
  );

  useEffect(() => {
    if (!breadcrumbs.length) {
      const navMain = getMainBreadcrumbBucket(4, null, navigate);
      navMain && setBreadCrumb([navMain]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breadcrumbs]);

  const selectedItemsMassUpdateValues = useMemo(() => {
    const itemsIds: number[] = selectedItems.map(item => item.id);

    const menuTypeIdsSet = new Set<number>();
    const categoryIdsSet = new Set<number>();
    const subcategoryIdsSet = new Set<number>();

    selectedItems.forEach(item => {
      item?.menuTypeId.forEach(id => menuTypeIdsSet.add(id));
      item?.categoryId.forEach(id => categoryIdsSet.add(id));
      item?.subcategoryId.forEach(id => subcategoryIdsSet.add(id));
    });

    return {
      menuTypesIds: Array.from(menuTypeIdsSet),
      categoryIds: Array.from(categoryIdsSet),
      subcategoryIds: Array.from(subcategoryIdsSet),
      itemsIds,
    };
  }, [selectedItems]);

  const handleInactivateModItems = useCallback(() => {
    setShowConfirmationModal({
      active: true,
      title: t('menuScreen.comboDetails.validationFields.itemGroups.items'),
      type: 'inactiveMultiple',
      data: selectedItems,
      btnCommonCsx: { minWidth: '120px', paddingInline: '12px' },
      onSuccess: async () => {
        resetChangeMassUpdateData();
        itemsMassUpdateStatus(selectedItemsMassUpdateValues, false);
        setShowConfirmationModal(CONFIRMATION_MODAL_INITIAL_STATE);
        const inactiveResponse = await itemMassUpdateWeb(onRefresh);
        // @ts-expect-error itemMassUpdateWeb returns a boolean
        if (inactiveResponse) {
          resetChangeMassUpdateData();
          setRunUnSelectAllRows(true);
        }
      },
      onCancel: () => {
        resetChangeMassUpdateData();
        setShowConfirmationModal(CONFIRMATION_MODAL_INITIAL_STATE);
      },
    });
  }, [
    selectedItems,
    selectedItemsMassUpdateValues,
    t,
    itemsMassUpdateStatus,
    onRefresh,
    resetChangeMassUpdateData,
    itemMassUpdateWeb,
    setShowConfirmationModal,
  ]);

  const unSelectAllRows = useCallback(
    () => setRunUnSelectAllRows(true),
    [setRunUnSelectAllRows],
  );

  const handleDeleteModItems = useCallback(() => {
    setShowConfirmationModal({
      active: true,
      title: t('menuScreen.comboDetails.validationFields.itemGroups.items'),
      type: 'deleteMultiple',
      data: selectedItems,
      btnCommonCsx: { minWidth: '120px', paddingInline: '12px' },
      onSuccess: async () => {
        setShowConfirmationModal(CONFIRMATION_MODAL_INITIAL_STATE);
        const deleteResponse = await deleteBucketItemsWeb(
          selectedItems.map(item => item.id),
        );
        // @ts-expect-error deleteBucketItemsWeb returns a boolean
        if (deleteResponse) {
          unSelectAllRows();
        }
      },
    });
  }, [
    selectedItems,
    deleteBucketItemsWeb,
    unSelectAllRows,
    setShowConfirmationModal,
    t,
  ]);

  const handleAddNewModItem = () => {
    addBreadcrumbLocationWeb({
      action: BreadCrumbAction.ADD,
      text: t('menuScreen.menuLocation.actionButtons.addNewItemTag'),
      onPress: () => navigate('/menu/items/add'),
      pathURL: '/menu/items/add',
    });
  };

  const actionDropdownOptions = useMemo(
    () => {
      const actions = selectingMode ? (
        <Button
          variant="danger"
          icon={<Icon name="FaTrashAlt" />}
          csx={{ width: '100%' }}
          onClick={
            isMasterMode ? handleDeleteModItems : handleInactivateModItems
          }
          disabled={selectedItems.length < 1}
        />
      ) : (
        [
          {
            text: t('menuScreen.menuLocation.actionButtons.addNewItem'),
            handler: handleAddNewModItem,
          },
        ]
      );

      return actions;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, isMasterMode, selectingMode, selectedItems],
  );

  const handleItemAssociation = (buttons: IItemSelection[]) => {
    assignItemsToLocationWeb(
      buttons.map(button => Number(button.id)),
      'items',
    );
    setAdditionModalState(false);
  };

  useEffect(() => {
    unSelectAllRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuVersion]);

  const handleMassUpdate = useCallback(() => {
    itemMassUpdateConfirmationWeb(
      selectedItemsMassUpdateValues.itemsIds.length,
      selectedItemsMassUpdateValues,
      () => {},
    );
  }, [itemMassUpdateConfirmationWeb, selectedItemsMassUpdateValues]);

  return (
    <>
      <ItemMassUpdateModal
        isActive={massUpdate}
        onClose={() => setMassUpdate(false)}
        onCancel={() => setMassUpdate(false)}
      />
      <SelectionModal
        type="items"
        mainScreenProps={{ entity: 'items' }}
        active={additionModalState}
        onModalClose={() => setAdditionModalState(false)}
        onAssociate={handleItemAssociation}
      />

      <MainScreenWithFilterContainer
        dropdowns={dropdowns}
        onFilterChange={(id, value) => {
          if (id === 'menuTypeId') {
            setSelectedMenuTypeIds(value);
          }
        }}
        sortAvailable={!showListView}
        showChildren={showListView}
        customButtons={
          <Grid.Item
            mb={10}
            sm={showListView ? 10 : 6}
            md={showListView ? 10 : 6}
            lg={showListView ? 9 : 4}
            xl={showListView ? 10 : 5}>
            <Box
              csx={{
                display: 'flex',
                alignItems: 'center',
                height: '100%',
                gap: 10,
              }}>
              <Divider csx={{ height: '100%' }} direction="vertical" />
              <Button
                variant="primary"
                disabled={
                  !canMassUpdate ||
                  (selectingMode ? selectedItems.length < 1 : false)
                }
                onClick={() => {
                  selectingMode ? handleMassUpdate() : setMassUpdate(true);
                }}
                onClickDisabled={() =>
                  !canMassUpdate &&
                  appDispatch(
                    showToast({
                      title: 'Missing Permissions',
                      description:
                        'The currently logged in role does not have permissions to perform this action',
                      type: 'warning',
                      isActive: true,
                    }),
                  )
                }
                icon={<Icon name="CustomLayers" color="white" />}>
                {t('menuScreen.itemsMain.massUpdate.button')}
              </Button>
              <Button
                variant={selectingMode ? 'active' : 'secondary'}
                onClick={() => {
                  unSelectAllRows();
                  setSelectingMode(currValue => !currValue);
                }}
                icon={<Icon name="FaDotCircle" color="textWhite" />}>
                {t('menuScreen.itemsMain.multiselect.button')}
              </Button>
              {selectingMode && (
                <Typography>{selectedItems.length} Items Selected</Typography>
              )}
            </Box>
          </Grid.Item>
        }
        breakpoint="medium"
        data={itemCardList}
        optionList={actionDropdownOptions}
        noItemsText={t('commonTexts.noneFound', {
          elementName: t('screenNames.ItemsScreen'),
        })}
        groupFilters
        optionalFiltersMatch={['upc']}
        filtersInCustomButtons
        renderItem={item => {
          const pathURL = `/menu/items/${item.id}`;
          return (
            <Card.Item
              {...item}
              isActive={true}
              showStatus={false}
              onClick={openInNewTab => {
                if (openInNewTab) return openNewTabWithOrgData(pathURL);

                addBreadcrumbLocationWeb({
                  action: BreadCrumbAction.NAV,
                  text: item.title,
                  onPress: () => {
                    navigate(pathURL, {
                      state: { menuTypeId: selectedMenuTypeIds },
                    });
                  },
                  statusPath: `selectedMode.currentMenu.items.${item.id}.active`,
                  pathURL,
                });
              }}
              isLink={getPathWithOrgData(pathURL)}
            />
          );
        }}>
        {filterParams => (
          <ItemsTable
            filterParams={filterParams}
            isSelectionMode={selectingMode}
            getFullValueOnRowSelectionChange
            setSelectedRows={setSelectedItems as Dispatch<any>}
            selectedRows={selectedItems}
            runUnSelectAllRows={runUnSelectAllRows}
            stopUnSelectAllRows={() => setRunUnSelectAllRows(false)}
          />
        )}
      </MainScreenWithFilterContainer>
    </>
  );
};

export default ItemsList;
